import React, { useContext, useState } from "react";
import i18next from "i18next";
import {
  Grid,
  Box,
} from "@mui/material";
import EADialog from "../../../../components/common/EADialog";
import RuleForm from "../form/RuleForm";
import {ERROR, SUCCESS, WARNING} from "../../../../services/common/Constants";
import { Context } from "../../../../states/Store";
import { evaluateRule, toggleRuleStatus } from "../../../../services/edge/RuleService";
import RuleEditConfirmDialog from "./RuleEditConfirmDialog";
import RuleSummary from "../steps/RuleSummary";
import RuleConfiguration from "../steps/RuleConfiguration";
import RuleAction from "../steps/RuleAction";
import { RuleFormSteps } from "services/common/RulesUtils";
import { useRequestLoading } from "components/common/hooks/useRequestLoading";

const FORM_ID = "rule-update-form-id";

const RuleEditDialog = ({
  isOpen,
  rule,
  onClose,
  onValidateSuccess,
  onDeleteOpen,
  readOnly = false
}) => {
  const [state, dispatch] = useContext(Context);
  const [currentStep, setCurrentStep] = useState(readOnly ? RuleFormSteps.SUMMARY : RuleFormSteps.CONFIGURATION); // CONFIGURATION, ACTION, SUMMARY, FINALISATION
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const { startLoading, stopLoading } = useRequestLoading();

  const [evaluatedRuleTargetResource, setEvaluatedRuleTargetResource] = useState();

  const handleToggleRuleStatus = async (rule) => {
    try {
      await toggleRuleStatus(state.account.id, rule.id, null);

      dispatch({
        type: "ALERT",
        alert: rule.enabled ?
          { type: WARNING, msg: i18next.t("rules.notifications.deactivate", { name: `${rule.name}` }) }
          :
          { type: SUCCESS, msg: i18next.t("rules.notifications.activate", { name: `${rule.name}` }) }
      });

      onValidateSuccess();
    } catch (error) {
      const err =
        error?.message?.messages?.error && error?.message?.messages?.error[0];
      dispatch({
        type: "ALERT",
        alert: { type: ERROR, msg: err?.code || "INTERNAL_ERROR" },
      });
    }
  };

  const getCustomDialogStyle = (step) => {
    const title = {
      marginBottom: "17px !important",
      fontSize: "25px",
      color: "#16161E",
      fontWeight: 600
    }

    return (
      step !== RuleFormSteps.FINALIZATION
        ? {
        backgroundColor: "#F2F3F7 !important",
        padding: "42px !important",
          "& h2": title
        }
        : {
        padding: "16px 28px !important",
          "& h2": title
      }
    )
  }

  const handleEvaluateRule = async (formData) => {
    try {
      startLoading();
      const response = await evaluateRule(state.account.id, formData, null);
      setEvaluatedRuleTargetResource(response.content);
    } catch (error) {
      const err =
        error?.message?.messages?.error && error?.message?.messages?.error[0];
      dispatch({
        type: "ALERT",
        alert: { type: ERROR, msg: err?.code || "INTERNAL_ERROR" },
      });
    } finally {
      stopLoading();
    }
  };

  return (
    <>
    <EADialog
      isOpen={isOpen}
      title={
        readOnly ? rule.name || i18next.t("read")
          : [RuleFormSteps.CONFIGURATION, RuleFormSteps.ACTION, RuleFormSteps.SUMMARY].includes(currentStep)
            ? i18next.t("rules.updateDialog.configurationStepTitle")
            : i18next.t("rules.updateDialog.finalizationStepTitle")
      }
      onCancel={() => {
        onClose();
      }}
      formId={FORM_ID}
      hasActions={false}
      fullWidth={true}
      maxWidth={[RuleFormSteps.CONFIGURATION, RuleFormSteps.ACTION, RuleFormSteps.SUMMARY].includes(currentStep) ? "popupxxl" : "popupsm"}
      customDialogStyle={getCustomDialogStyle(currentStep)}
      crossVisible={![RuleFormSteps.FINALIZATION].includes(currentStep)}
    >
      <RuleForm
        rule={rule}
        formId={FORM_ID}
        submitHandler={() => {/*Do nothing, the submit handler is passed to the final step*/ }}
      >
        <Box sx={{ display: [RuleFormSteps.CONFIGURATION, RuleFormSteps.ACTION, RuleFormSteps.SUMMARY].includes(currentStep) ? 'block' : 'none' }}>
          {/* <ConcernCounter count={5} type={"Tiers"} /> */}
          <Grid container spacing={"24px"}>
             <Grid item xs={12}>
              <RuleConfiguration
                open={currentStep === RuleFormSteps.CONFIGURATION || readOnly}
                onCancel={() => {
                  onClose()
                }}
                onContinue={(data) => {
                  setCurrentStep(RuleFormSteps.ACTION)
                  handleEvaluateRule(data)
                }}
                isEditionMode={true}
                name={rule.name}
                onRuleTypeChange={()=> setCurrentStep(RuleFormSteps.CONFIGURATION)}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <RuleAction
                open={currentStep === RuleFormSteps.ACTION || readOnly}
                onCancel={() => {
                  setCurrentStep(RuleFormSteps.CONFIGURATION)
                }}
                onContinue={() => {
                  setCurrentStep(RuleFormSteps.SUMMARY)
                }}
                disabled={currentStep === RuleFormSteps.CONFIGURATION}
                readOnly={readOnly}
              />
            </Grid>

          {rule && (
            <Grid item xs={12}>
              <RuleSummary
                open={currentStep === RuleFormSteps.SUMMARY}
                onCancel={
                  readOnly
                    ? () => onClose()
                    : () => { setCurrentStep(RuleFormSteps.ACTION) }
                }
                onContinue={() => setIsConfirmDialogOpen(true)}
                onDelete={() => {
                  onDeleteOpen();
                }}
                onDeactivate= {() => {
                  handleToggleRuleStatus(rule);
                }}
                disabled={(
                  currentStep === RuleFormSteps.CONFIGURATION ||
                   currentStep === RuleFormSteps.ACTION
                )}
                isEditionMode={true}
                isActiveRule={rule.enabled}
                evaluatedRuleTargetResource={evaluatedRuleTargetResource}
                readOnly={readOnly}
              />
            </Grid>
          )}
          </Grid>
        </Box>
      {/* PopUp : on edit confirm */}
      {isConfirmDialogOpen && (
        <RuleEditConfirmDialog
          rule={{...rule,
                  status: rule.enabled ? 'active' : 'inactive'}}
          isOpen={isConfirmDialogOpen}
          onClose={() => setIsConfirmDialogOpen(false)}
          onValidateSuccess={() => {
            onValidateSuccess();
            setIsConfirmDialogOpen(false);
          }}
        />
      )}
      </RuleForm>
    </EADialog>


    </>
    )
}

export default RuleEditDialog;
